import { useCallback, useState } from "react";
import type { BuilderApi, Workspace } from "../api/contract";
import { changeServing, servingRequest, type ServingChange, type ServingOutcome } from "../lib/serving";

export type ServingState =
  | { phase: "idle" }
  | { phase: "working"; change: ServingChange }
  | { phase: "failed"; change: ServingChange; outcome: Exclude<ServingOutcome, "done"> };

export interface UseServingChangeOptions {
  api: BuilderApi;
  siteId: string;
  workspace: Workspace | undefined;
  /** Reads what the server has now; resolves undefined when it can't be read. */
  refreshWorkspace: () => Promise<Workspace | undefined>;
  /** Called after a change applied, so the address card and history show it. */
  onChanged?: () => void;
}

/**
 * Takes the site offline or puts an earlier published version live. The owner confirms in the
 * interface first; this then prepares the change and confirms it with the server's exact digest.
 */
export function useServingChange({ api, siteId, workspace, refreshWorkspace, onChanged }: UseServingChangeOptions) {
  const [state, setState] = useState<ServingState>({ phase: "idle" });
  const epoch = workspace?.site.servingEpoch;

  const run = useCallback(async (change: ServingChange): Promise<boolean> => {
    if (epoch === undefined) return false;
    setState({ phase: "working", change });
    const outcome = await changeServing(api, siteId, servingRequest({ ...change }, { servingEpoch: epoch }), async () => (await refreshWorkspace())?.site.servingEpoch);
    if (outcome === "done") {
      setState({ phase: "idle" });
      await refreshWorkspace();
      onChanged?.();
      return true;
    }
    setState({ phase: "failed", change, outcome });
    return false;
  }, [api, siteId, epoch, refreshWorkspace, onChanged]);

  const clear = useCallback(() => setState({ phase: "idle" }), []);
  return { state, run, clear };
}

export type ServingController = ReturnType<typeof useServingChange>;

/** What to tell the owner when a change didn't go through. */
export function servingProblem(state: ServingState): string | null {
  if (state.phase !== "failed") return null;
  if (state.outcome === "changed") return "Your website changed in the meantime. Look at History again, then try again.";
  if (state.outcome === "unavailable") return "That version isn’t kept any more, so it can’t go live. Nothing was changed.";
  return state.change.kind === "unpublish" ? "Your site is still live. That didn’t go through, so try again." : "That didn’t go through. Nothing was changed. Try again.";
}
